import React from 'react';
import moment from "moment-timezone";
import {
    faCheck,
    faExclamationTriangle,
    faRefresh,
    faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

export function isInactiveIntegration(integration) {
    return integration?.status === "not_ready" && integration?.ready === false;
}

export function hasGoogleAdsPartialAccountAttention(integration) {
    return integration?.attentionCode === "google_ads_partial_account_unavailable";
}

export function hasGoogleAdsPartialAccountSnapshot(integration) {
    return getGoogleAdsPartialUnavailableAccounts(integration).length > 0
        || getGoogleAdsPartialSuccessfulAccounts(integration).length > 0;
}

export function getGoogleAdsPartialSuccessfulAccounts(integration) {
    return hasGoogleAdsPartialAccountAttention(integration)
        ? (integration?.successfulAccounts ?? [])
        : (integration?.recentSuccessfulAccounts ?? []);
}

export function getGoogleAdsPartialUnavailableAccounts(integration) {
    return hasGoogleAdsPartialAccountAttention(integration)
        ? (integration?.unavailableAccounts ?? [])
        : (integration?.recentUnavailableAccounts ?? []);
}

export function getGoogleAdsUnavailableReason(t, reason) {
    switch (reason) {
        case "customer_not_enabled":
            return t("runtime.integration.google_ads_partial.reason.customer_not_enabled", {defaultValue: "Účet je v Google Ads deaktivovaný."});
        case "permission_denied":
            return t("runtime.integration.google_ads_partial.reason.permission_denied", {defaultValue: "Připojený uživatel už k účtu nemá přístup."});
        case "not_available":
        default:
            return t("runtime.integration.google_ads_partial.reason.not_available", {defaultValue: "Účet už není dostupný."});
    }
}

export function getGoogleAdsAccountLabel(account) {
    return `[${account.id}] ${account.name || account.id}`;
}

export function getGoogleAdsAttentionBadgeLabel(t, successfulAccounts = []) {
    if (successfulAccounts.length === 0) {
        return t("runtime.integration.google_ads_partial.completed_with_warning", {defaultValue: "Dokončeno s upozorněním"});
    }

    return t("runtime.integration.google_ads_partial.badge", {defaultValue: "Částečně aktualizováno"});
}

export function formatRuntimeDate(value) {
    if (!value) {
        return null;
    }

    return moment(value).tz("Europe/Prague").format("D.M.YYYY HH:mm:ss");
}

export function formatRuntimeDay(value) {
    if (!value) {
        return null;
    }

    return moment(value).format("D.M.YYYY");
}

export function formatRuntimeScope(since, until) {
    if (!since && !until) {
        return null;
    }

    return `${formatRuntimeDate(since) ?? "?"} -> ${formatRuntimeDate(until) ?? "?"}`;
}

export function getRunModeLabel(t, mode, kind) {
    if (kind === 'historical_precalculation') {
        if (kind === 'historical_precalculation') {
            return React.createElement(
                'span',
                {className: 'text-blue-500'},
                t("runtime.scheduler.kind.historical_precalculation", {defaultValue: "Historický přepočet"})
            );
        }
    }

    switch (mode) {
        case "regular":
            return t("runtime.mode.regular", {defaultValue: "Pravidelný"});
        case "nightly":
            return t("runtime.mode.nightly", {defaultValue: "Noční"});
        case "monthly":
            return t("runtime.mode.monthly", {defaultValue: "Měsíční"});
        case "manual":
            return t("runtime.mode.manual", {defaultValue: "Ruční"});
        default:
            return mode;
    }
}

export function getFetchModeLabel(t, mode) {
    switch (mode) {
        case "day":
            return t("runtime.fetch.day", {defaultValue: "1 den"});
        case "two_days":
            return t("runtime.fetch.two_days", {defaultValue: "2 dny"});
        case "week":
            return t("runtime.fetch.week", {defaultValue: "Týden"});
        case "month":
            return t("runtime.fetch.month", {defaultValue: "Měsíc"});
        case "two_months":
            return t("runtime.fetch.two_months", {defaultValue: "2 měsíce"});
        case "quarter":
            return t("runtime.fetch.quarter", {defaultValue: "Čtvrtletí"});
        case "half_year":
            return t("runtime.fetch.half_year", {defaultValue: "Půl roku"});
        case "year":
            return t("runtime.fetch.year", {defaultValue: "1 rok"});
        case "custom":
            return t("runtime.fetch.custom", {defaultValue: "Vlastní rozsah"});
        default:
            return mode;
    }
}

export function getProjectPhaseLabel(t, phase) {
    switch (phase) {
        case "fetching":
            return t("runtime.project.phase.fetching", {defaultValue: "Stahuji data"});
        case "queued":
            return t("runtime.project.phase.queued", {defaultValue: "Čeká na zpracování"});
        case "pending":
        case "pairing":
        case "building":
        case "publishing":
            return t("runtime.project.phase.recalculating", {defaultValue: "Zpracovávám data"});
        default:
            return null;
    }
}

export function getIntegrationPhaseLabel(t, phase) {
    switch (phase) {
        case "webhook":
            return t("runtime.integration.phase.webhook", {defaultValue: "Změny přicházejí webhookem"});
        case "fetch_pending":
            return t("runtime.integration.phase.fetch_pending", {defaultValue: "Čeká na fetch slot"});
        case "fetching":
            return t("runtime.integration.phase.fetching", {defaultValue: "Stahuji data"});
        case "waiting_for_publish":
            return t("runtime.integration.phase.waiting_for_publish", {defaultValue: "Čeká na publikaci projektu"});
        default:
            return null;
    }
}

export function getRuntimeErrorMessage(t, errorCode) {
    switch (errorCode) {
        case "configuration_error":
            return t("runtime.integration.errors.configuration_error", {defaultValue: "Data se nepodařilo aktualizovat kvůli nastavení připojení. Správce musí zkontrolovat konfiguraci integrace."});
        case "oauth_reconnect_required":
            return t("runtime.integration.errors.oauth_reconnect_required", {defaultValue: "Platnost připojení skončila nebo byla odvolána. Správce musí obnovit připojení integrace."});
        case "permission_denied":
            return t("runtime.integration.errors.permission_denied", {defaultValue: "Data se nepodařilo aktualizovat, protože připojený účet nemá přístup ke zdroji dat. Správce musí zkontrolovat připojení a oprávnění k vybranému účtu."});
        case "rate_limited":
            return t("runtime.integration.errors.rate_limited", {defaultValue: "Poskytovatel dočasně omezil počet požadavků. Aktualizaci zkusíme znovu později."});
        case "timeout":
            return t("runtime.integration.errors.timeout", {defaultValue: "Poskytovatel dat neodpověděl včas. Aktualizaci zkusíme znovu později."});
        case "provider_unavailable":
            return t("runtime.integration.errors.provider_unavailable", {defaultValue: "Poskytovatel dat je dočasně nedostupný. Aktualizaci zkusíme znovu později."});
        case "unknown":
        default:
            return t("runtime.integration.errors.unknown", {defaultValue: "Data se teď nepodařilo aktualizovat. Zkusíme to znovu při další aktualizaci."});
    }
}

export function getProjectRuntimeAppearance(t, projectRuntime) {
    if (projectRuntime.status === "failed") {
        return {
            color: "rose",
            icon: faExclamationTriangle,
            label: t("runtime.project.failed", {defaultValue: "Chyba"}),
            spin: false,
        };
    }

    if (projectRuntime.status === "queued") {
        return {
            color: "emerald",
            icon: faCheck,
            label: projectRuntime.lastPublishedAt
                ? t("runtime.project.updated", {defaultValue: "Aktualizováno"})
                : null,
            spin: false,
        };
    }

    if (projectRuntime.status === "running") {
        return {
            color: "yellow",
            icon: faRefresh,
            label: getProjectPhaseLabel(t, projectRuntime.phase) ?? t("runtime.project.running", {defaultValue: "Probíhá"}),
            spin: true,
        };
    }

    return {
        color: "emerald",
        icon: faCheck,
        label: projectRuntime.lastPublishedAt
            ? t("runtime.project.updated", {defaultValue: "Aktualizováno"})
            : null,
        spin: false,
    };
}

export function getIntegrationRuntimeAppearance(t, integration) {
    if (integration.status === "failed") {
        return {
            color: "rose",
            icon: faExclamationTriangle,
            label: t("runtime.integration.failed", {defaultValue: "Chyba"}),
            spin: false,
        };
    }

    if (integration.status === "not_ready") {
        return {
            color: "yellow",
            icon: faTriangleExclamation,
            label: t("runtime.integration.not_ready", {defaultValue: "Není připravena"}),
            spin: false,
        };
    }

    if (integration.status === "running") {
        return {
            color: "yellow",
            icon: faRefresh,
            label: integration.phase === "fetching"
                ? t("runtime.integration.phase.fetching", {defaultValue: "Stahuji data"})
                : null,
            spin: true,
        };
    }

    return {
        color: "emerald",
        icon: faCheck,
        label: null,
        spin: false,
    };
}

export function getOverviewIndicator(t, overview) {
    if (!overview) {
        return {
            colorClass: "text-slate-400",
            icon: faRefresh,
            spin: true,
        };
    }

    const {projectRuntime, integrations} = overview;
    const activeIntegrations = (integrations ?? []).filter((integration) => !isInactiveIntegration(integration));

    if (
        projectRuntime?.status === "failed"
        || activeIntegrations.some((integration) => integration.status === "failed")
    ) {
        return {
            colorClass: "text-datixo-negative",
            icon: faExclamationTriangle,
            spin: false,
        };
    }

    if (
        projectRuntime?.status === "queued"
        || projectRuntime?.status === "running"
        || activeIntegrations.some((integration) => integration.status === "queued" || integration.status === "running")
    ) {
        return {
            colorClass: "text-cyan-600",
            icon: faRefresh,
            spin: true,
        };
    }

    if (activeIntegrations.some(hasGoogleAdsPartialAccountAttention)) {
        return {
            colorClass: "text-amber-500",
            icon: faTriangleExclamation,
            spin: false,
        };
    }

    if (activeIntegrations.some((integration) => integration.status === "not_ready")) {
        return {
            colorClass: "text-amber-500",
            icon: faTriangleExclamation,
            spin: false,
        };
    }

    return {
        colorClass: "text-green-600 dark:text-green-500",
        icon: faCheck,
        spin: false,
    };
}

export function getProjectRuntimeGroup(projectWithRuntime) {
    const {projectRuntime, integrations} = projectWithRuntime;
    const activeIntegrations = integrations.filter((integration) => !isInactiveIntegration(integration));

    if (
        projectRuntime.status === "failed"
        || activeIntegrations.some((integration) => integration.status === "failed" || integration.status === "not_ready")
    ) {
        return "alert";
    }

    if (
        projectRuntime.status === "queued"
        || projectRuntime.status === "running"
        || activeIntegrations.some((integration) => integration.status === "queued" || integration.status === "running")
    ) {
        return "active";
    }

    return "healthy";
}

export function sortProjectsByRuntime(projects) {
    const groupWeight = {
        alert: 0,
        active: 1,
        healthy: 2,
    };

    return [...projects].sort((left, right) => {
        const leftGroup = getProjectRuntimeGroup(left);
        const rightGroup = getProjectRuntimeGroup(right);
        const leftWeight = groupWeight[leftGroup] ?? 99;
        const rightWeight = groupWeight[rightGroup] ?? 99;

        if (leftWeight !== rightWeight) {
            return leftWeight - rightWeight;
        }

        return left.projectData.name.localeCompare(right.projectData.name, "cs");
    });
}

export function sortIntegrationsByRuntime(integrations, isPrimaryDataIntegration = () => false) {
    const statusWeight = {
        failed: 0,
        not_ready: 1,
        running: 2,
        queued: 3,
        idle: 4,
    };

    return [...integrations].sort((left, right) => {
        const leftErp = isPrimaryDataIntegration(left);
        const rightErp = isPrimaryDataIntegration(right);

        if (leftErp !== rightErp) {
            return leftErp ? -1 : 1;
        }

        const leftInactive = isInactiveIntegration(left);
        const rightInactive = isInactiveIntegration(right);

        if (leftInactive !== rightInactive) {
            return leftInactive ? 1 : -1;
        }

        const leftWeight = statusWeight[left.status] ?? 99;
        const rightWeight = statusWeight[right.status] ?? 99;

        if (leftWeight !== rightWeight) {
            return leftWeight - rightWeight;
        }

        return left.integrationName.localeCompare(right.integrationName, "cs");
    });
}
